// Eskom PARCC — admin panel to maintain the report's supplementary registers
// (owner feedback point 4 full capture): the RFI Register (bulk-imported) and
// the per-operating-unit RTM sign-off status. Admin-only (rendered inside the
// admin-gated Reports screen). Data feeds the generated report's RFI + RTM pages.
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Button,
  DataTable,
  Pager,
  usePagination,
  useToast,
  type Column,
} from '../../components/ui';
import {
  useRfiRegister,
  useImportRfi,
  useReportRuns,
} from '../../hooks';
import { parseToRecords, type ImportRecord } from '../import/importParse';
import { downloadStorageObject } from '../../lib/storage';
import { adminApi } from '../../lib/adminApi';
import { mapReportRunRow, type ReportRunEntry, type DcReportRunRow } from '../../lib/db';
import type { RfiInput } from '../../lib/dbWrite';
import type { RfiRow } from '../../data/types';

/** Find a record value by the first header whose normalised form contains all keywords. */
function findVal(record: ImportRecord, ...keywords: string[]): string {
  const key = Object.keys(record).find((k) => {
    const n = k.toLowerCase();
    return keywords.every((kw) => n.includes(kw));
  });
  return key ? String(record[key] ?? '').trim() : '';
}

/** Map a parsed RFI spreadsheet record → RfiInput (lenient header matching). */
function recordToRfi(record: ImportRecord): RfiInput {
  return {
    rfiRef: findVal(record, 'rfi', 'ref') || findVal(record, 'rfi'),
    division: findVal(record, 'division') || findVal(record, 'operating'),
    supplier: findVal(record, 'supplier'),
    contractNumber: findVal(record, 'contract', 'number') || findVal(record, 'contract', 'no'),
    contractValue: findVal(record, 'contract', 'value') || findVal(record, 'value'),
    dateReceivedClient: findVal(record, 'received', 'client'),
    dateReceivedConsultant: findVal(record, 'received', 'consultant'),
    dateSubmittedDeloitte: findVal(record, 'submitted', 'deloitte') || findVal(record, 'submitted'),
    concerns: findVal(record, 'concern') || findVal(record, 'issue'),
    status: findVal(record, 'status'),
  };
}

const RFI_COLUMNS: Column<RfiRow>[] = [
  { key: 'rfiRef', header: 'RFI Ref', sortable: true, render: (r) => r.rfiRef ?? '—' },
  { key: 'division', header: 'Division', sortable: true, render: (r) => r.division ?? '—' },
  { key: 'supplier', header: 'Supplier', sortable: true, render: (r) => r.supplier ?? '—' },
  { key: 'contractNumber', header: 'Contract No', render: (r) => r.contractNumber ?? '—' },
  { key: 'contractValue', header: 'Value', align: 'right', render: (r) => r.contractValue ?? '—' },
  { key: 'concerns', header: 'Concerns', render: (r) => r.concerns ?? '—' },
];

export function ReportRegisterPanel(): React.JSX.Element {
  const { show } = useToast();
  const rfiQuery = useRfiRegister();
  const importRfi = useImportRfi();
  const fileRef = useRef<HTMLInputElement>(null);



  async function handleRfiFile(file: File): Promise<void> {
    try {
      const isCsv = /\.csv$/i.test(file.name);
      const records = isCsv
        ? parseToRecords(await file.text())
        : parseToRecords(await file.arrayBuffer());
      const rows = records
        .map(recordToRfi)
        .filter((r) => r.rfiRef || r.supplier || r.contractNumber);
      if (!rows.length) {
        show('No usable RFI rows found in the file.', 'error');
        return;
      }
      importRfi.mutate(rows);
    } catch (err) {
      show(err instanceof Error ? err.message : 'Could not read the RFI file.', 'error');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const rfiRows = rfiQuery.data ?? [];
  // Page the register — the RFI list grows with every division query raised.
  const rfiPages = usePagination(rfiRows);

  return (
    <>
      <Card
        title="RFI register"
        subtitle="Bulk-import the Requests-for-Information register (replace-all). It feeds the report's RFI summary + register pages."
        actions={
          <div className="actions">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm,.xls,.csv"
              aria-label="Import RFI register file"
              disabled={importRfi.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleRfiFile(f);
              }}
            />
            {rfiRows.length > 0 && (
              <Button
                variant="ghost"
                disabled={importRfi.isPending}
                onClick={() => {
                  importRfi.reset();
                  void importRfi
                    .mutateAsync([])
                    .then(() => show('RFI register cleared.', 'success'));
                }}
              >
                Clear register
              </Button>
            )}
          </div>
        }
      >
        <div className="small" style={{ marginBottom: 8 }}>
          {importRfi.isPending
            ? 'Importing…'
            : `${rfiRows.length.toLocaleString('en-ZA')} RFI row(s) on record.`}
        </div>
        {rfiRows.length > 0 && (
          <>
            <DataTable columns={RFI_COLUMNS} rows={rfiPages.slice} getRowKey={(r) => r.id} />
            <Pager pagination={rfiPages} noun="RFI" />
          </>
        )}
      </Card>

      <GeneratedReportsCard />
    </>
  );
}

/** The last segment of a Storage object path, used as the download file name. */
function basename(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] || 'report';
}

/** Format an ISO timestamp as a compact en-GB date-time. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The shared report-archive table: number + scope + generator + as-of date, with
 * a re-download of the exact stored artifact (so the issued report — and the
 * numbers it carried on its date — are retained, feedback 2c). An "as-of date"
 * filter lets a user pull the report(s) for a specific date.
 */
/** Friendly archive Type label for a ReportRun.type. */
function reportTypeLabel(type: string): string {
  if (type === 'weekly_deck') return 'Deck';
  if (type === 'rtm') return 'RTM';
  if (type === 'audit_recovery_program') return 'Legacy';
  return type;
}

function ReportRunsTable({
  runs,
  isLoading,
  emptyText,
}: {
  runs: readonly ReportRunEntry[];
  isLoading: boolean;
  emptyText: string;
}): React.JSX.Element {
  const { show } = useToast();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [asOf, setAsOf] = useState('');

  async function retrieve(run: ReportRunEntry): Promise<void> {
    if (!run.storagePath) return;
    setDownloading(run.id);
    try {
      await downloadStorageObject(run.storagePath, basename(run.storagePath));
    } catch (err) {
      show(err instanceof Error ? err.message : 'Could not retrieve the report.', 'error');
    } finally {
      setDownloading(null);
    }
  }

  const filtered = useMemo(
    () => (asOf ? runs.filter((r) => (r.asOfDate ?? '').slice(0, 10) === asOf) : [...runs]),
    [runs, asOf],
  );
  // Page the archive — every generated report is kept, so this only grows.
  const archivePages = usePagination(filtered);

  const columns: Column<ReportRunEntry>[] = [
    {
      key: 'reportNumber',
      header: 'Report no.',
      sortable: true,
      render: (r) => r.reportNumber ?? '—',
    },
    { key: 'asOfDate', header: 'As of', sortable: true, render: (r) => r.asOfDate ?? '—' },
    { key: 'type', header: 'Type', sortable: true, render: (r) => reportTypeLabel(r.type) },
    { key: 'division', header: 'Division', sortable: true, render: (r) => r.division || 'All' },
    {
      key: 'generatedAt',
      header: 'Generated',
      sortable: true,
      render: (r) => formatDateTime(r.generatedAt),
    },
    {
      key: 'generatedByEmail',
      header: 'By',
      sortable: true,
      render: (r) => r.generatedByEmail ?? '—',
    },
    { key: 'format', header: 'Format', render: (r) => (r.format ? r.format.toUpperCase() : '—') },
    {
      key: 'storagePath',
      header: 'Document',
      render: (r) =>
        r.storagePath ? (
          <Button disabled={downloading === r.id} onClick={() => void retrieve(r)}>
            {downloading === r.id ? 'Retrieving…' : 'Download'}
          </Button>
        ) : (
          <span className="small">Download only</span>
        ),
    },
  ];

  return (
    <>
      <label style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
        <span className="small">Pull to a date</span>
        <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </label>
      {isLoading ? (
        <div className="small">Loading the report archive…</div>
      ) : filtered.length === 0 ? (
        <div className="small">{asOf ? 'No reports issued on that date.' : emptyText}</div>
      ) : (
        <>
          <DataTable columns={columns} rows={archivePages.slice} getRowKey={(r) => r.id} />
          <Pager pagination={archivePages} noun="report" />
        </>
      )}
    </>
  );
}

/**
 * Register of generated reports (admin). Surfaces the server-stamped report number,
 * generator + timestamp; a persisted run (storagePath present) can be re-retrieved.
 */
function GeneratedReportsCard(): React.JSX.Element {
  const runsQuery = useReportRuns();
  return (
    <Card
      title="Generated reports"
      subtitle="Every report run, numbered and server-stamped with who generated it and when. Retained documents can be re-retrieved — the numbers they carried on their date are preserved."
    >
      <ReportRunsTable
        runs={runsQuery.data ?? []}
        isLoading={runsQuery.isLoading}
        emptyText="No reports have been generated yet."
      />
    </Card>
  );
}

/**
 * A division's report archive (feedback 2c) for team leads (their own division)
 * and managers (a chosen division). PullReportRuns is admin-only, so this reads
 * through the server-scoped admin-api endpoint instead.
 */
export function DivisionalReportArchive({ division }: { division: string }): React.JSX.Element {
  const query = useQuery<ReportRunEntry[], Error>({
    queryKey: ['reportRuns', 'divisional', division],
    queryFn: async () => {
      const res = await adminApi.reportsDivisional(division);
      return (res.reportRuns as DcReportRunRow[]).map(mapReportRunRow);
    },
    enabled: division.length > 0,
  });
  return (
    <Card
      title={`Report archive — ${division || 'division'}`}
      subtitle="Your division's issued reports, numbered and retained. Re-download a previously issued report — the numbers it carried on its date are preserved."
    >
      {division.length === 0 ? (
        <div className="small">Choose a division to view its report archive.</div>
      ) : (
        <ReportRunsTable
          runs={query.data ?? []}
          isLoading={query.isLoading}
          emptyText="No reports have been issued for this division yet."
        />
      )}
    </Card>
  );
}
